import React, { useEffect, useState } from "react";
import { Card } from "./card";
import { ArrowRight } from "lucide-react";
import ModernFilePicker from "./support_admin/modernfilepicker.tsx";
import { useNavigate } from "react-router-dom";


export default function EventModules({ from, appearance, setAppearance, baseUrl, currentUser, setHeroData, events }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // --- API LOAD (can be replaced with real Chamcore endpoint) ---
  useEffect(() => {
    async function load() {
      // Fake API delay
      await new Promise((res) => setTimeout(res, 800));
      setModules([
        {
          id: "live",
          title: "Streaming nå",
          subtitle: "Se direktesendte foredrag og livesendinger",
          image: events[0]?.image ? events[0].image :
            "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?auto=format&fit=crop&w=1200&q=80",
          link: "#live",
          gradient:
            "from-purple-600/70 via-fuchsia-600/60 to-purple-700/70",
        },
        {
          id: "upcoming",
          title: "Kommende eventer",
          subtitle: "Webinarer og arrangementer som starter snart",
          image: events[1]?.image ? events[1].image :
            "https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&w=1200&q=80",
          link: "#upcoming",
          gradient:
            "from-blue-600/70 via-indigo-600/60 to-blue-700/70",
        },
        {
          id: "agents",
          title: "Våre veiledere",
          subtitle: "Møt vårt profesjonelle rådgiverteam",
          image: events[2]?.image ? events[2].image :
            "https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=1200&q=80",
          link: "#agents",
          gradient:
            "from-emerald-600/70 via-teal-600/60 to-emerald-700/70",
        },
      ]);
      setLoading(false);
    }
    load();
  }, [events]);

  // --- PARALLAX TILT ---
  function tilt(e) {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / 20) * -1;
    const rotateY = (x - centerX) / 20;
    card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  }

  function resetTilt(e) {
    const card = e.currentTarget;
    card.style.transform = "rotateX(0deg) rotateY(0deg)";
  }

  // --- SHIMMER LOADER COMPONENT ---
  const Shimmer = () => (
    <div className="w-full h-72 rounded-3xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 animate-shimmer" />
  );

  const handleEventClick = (to) => {
    if (from === "support-client") {
      return;
    }
    if (to === "live") {
      to = "streaming-now";
    } else if (to === "upcoming") {
      to = "upcoming-events";
    } else {
      to = "agents";
    }
    navigate(`/${to}`, {
      state: { scrollPosition: window.scrollY }
    });
  }

  const updateModule = (id, patch) => {
    setModules(prev =>
      prev.map(m => (m.id === id ? { ...m, ...patch } : m))
    );
    setHeroData(prev => ({
      ...prev,
      events: prev.events.map(m => (m.id === id ? { ...m, ...patch } : m)),
    }));
  };

  return (
    <>
      <div className="w-full flex justify-center py-20 bg-gradient-to-b from-white to-slate-50 animate-fadeIn">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 max-w-7xl w-full px-6">
          {(loading ? Array(3).fill({}) : modules).map((m, i) => (
            <>
              <div className="block">
                <Card
                  onMouseMove={tilt}
                  onMouseLeave={resetTilt}
                  className="rounded-3xl overflow-hidden shadow-lg transition-all duration-500 bg-white/90 backdrop-blur-xl border border-white/30 relative cursor-pointer"
                  style={{ transformStyle: "preserve-3d", transition: "all 0.3s" }}
                  key={m.id || i} onClick={() => handleEventClick(m.id)}
                >
                  {/* Animated Gradient Border */}
                  <div className="absolute inset-0 rounded-3xl p-[2px] animate-borderGlow pointer-events-none"
                    style={
                      appearance.gradientEnabled
                        ? {
                          backgroundImage: `linear-gradient(to right, ${appearance.gradientFrom}, ${appearance.gradientTo})`,
                        }
                        : {
                          backgroundColor: appearance.gradientTo,
                        }
                    }
                  />

                  <div className="relative w-full h-72 overflow-hidden rounded-3xl shadow-inner">
                    {m.id === "live" && !loading && (
                      <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md animate-pulse z-10">
                        LIVE
                      </div>
                    )}

                    {/* SHIMMER OR IMAGE */}
                    {loading ? (
                      <Shimmer />
                    ) : (
                      <>
                        <img
                          src={m.image}
                          alt={m.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                        />
                      </>
                    )}

                    {!loading && appearance.gradientEnabled && (
                      <div
                        className={`absolute inset-0 opacity-60 group-hover:opacity-80 transition-all duration-500`}
                        style={{ backgroundImage: `linear-gradient(to right, ${appearance.gradientFrom}, ${appearance.gradientTo})`, }}
                      />
                    )}

                    {!loading && (
                      <div className="absolute bottom-4 left-4 right-4 text-white drop-shadow-xl">
                        <h2 className="text-xl font-bold mb-1 leading-tight">
                          {m.title}
                        </h2>
                        <p className="text-sm opacity-90 leading-snug">{m.subtitle}</p>
                      </div>
                    )}
                  </div>

                  {!loading && (
                    <div className="p-5 text-left">
                      <div className="inline-flex items-center gap-2 text-purple-700 font-medium text-sm group-hover:text-purple-900 transition">
                        Utforsk
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  )}
                </Card>

                {
                  from === "support-client" && (
                    <ModernFilePicker
                      label="Dra og slipp her"
                      accept="image/*"
                      multiple={false}   // ✅ SINGLE FILE MODE
                      onFile={async (file) => {
                        if (!file) return;

                        const formData = new FormData();
                        formData.append("image", file);
                        formData.append("token", currentUser?.token);

                        try {
                          const res = await fetch(baseUrl + "/eventModulesPicture", {
                            method: "POST",
                            body: formData,
                          });

                          const { data } = await res.json();

                          // ✅ Update UI instantly
                          updateModule(m.id, { image: data });
                        } catch (err) {
                          console.error("Upload failed", err);
                        }
                      }}
                    />
                  )
                }
              </div>
            </>
          ))}

        </div>
      </div>
      <div className="w-full flex justify-center bg-slate-50 animate-fadeIn">
        <div className="flex justify-end max-w-7xl w-full px-6">
          <a href="/video-service" className="flex items-center rounded-2xl text-dark px-5 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--secondary-color)" }}>Video service</a>
        </div>
      </div>
    </>
  );
}
